import type { AppConfig } from '@/types/config'

export const bibleConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1'
  },
  app: {
    id: 'bible',
    name: 'The Bible Plus',
    description: 'Biblical Teachings and Spiritual Growth',
    contentId: 3,
    subscriptionPlanId: 3,
    logo: {
      default: '/assets/images/bible/logo.svg',
      icon: '/assets/images/bible/icon.svg'
    }
  },

  theme: {
    colors: {
      primary: '#8B4513', // Saddle brown (139, 69, 19)
      secondary: '#2F4F4F', // Dark slate gray (47, 79, 79)
      accent: '#DAA520', // Goldenrod (218, 165, 32)
      background: '#F5F5DC' // Beige (245, 245, 220)
    },
    dark: {
      primary: '#FFFFFF', // White for dark mode
      secondary: '#8B4513', // Same saddle brown
      accent: '#DAA520', // Same goldenrod
      background: '#2F4F4F' // Dark slate gray
    },
    fonts: {
      heading: 'Poppins',
      body: 'Poppins'
    },
    form: {
      padding: {
        container: 'py-6 px-3 sm:px-8',
        field: 'space-y-3'
      },
      rounding: {
        button: 'rounded-sm',
        input: 'rounded-sm'
      }
    }
  },

  layout: {
    maxWidth: '2xl',
    headerHeight: '16',
    playerHeight: '72px',
    screens: {
      home: [
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Daily Might",
            "list": [],
            "params": {
              "filter[category]": "Daily Might"
            }
          },
          "title": "Daily Might",
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Topic",
            "list": [],
            "params": {
              "limit": 8,
            }
          },
          "title": "Browse by Topic",
          "limit": 8
        },
      ],
      search: [
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Biblical Topics",
            "list": [],
            "params": {
              "limit": 8,
            }
          },
          "title": "Biblical Topics",
          "limit": 8
        }
      ],
      library: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "recently_played",
          "data": {
            "title": "Recently Played",
            "list": [],
            "params": {
              "filter[library]": "played"
            }
          },
          "title": "Recently Played"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "liked",
          "data": {
            "title": "Favorites",
            "list": [],
            "params": {
              "filter[library]": "liked"
            }
          },
          "title": "Favorites"
        },
        {
          "type": "titles",
          "layout": "grid",
          "id": "purchased",
          "data": {
            "title": "My Spiritual Library",
            "list": [],
            "params": {
              "filter[library]": "purchased"
            }
          },
          "title": "My Spiritual Library"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "bookmarked",
          "data": {
            "title": "Reading List",
            "list": [],
            "params": {
              "filter[library]": "bookmarked"
            }
          },
          "title": "Reading List"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "finished",
          "data": {
            "title": "Completed Studies",
            "list": [],
            "params": {
              "filter[library]": "finished"
            }
          },
          "title": "Completed Studies"
        }
      ]
    }
  },

  features: {
    shop: true,
    ads: false,
    guestMode: true,
    favorites: true,
    darkMode: true,
    search: true,
    player: true,
    sharing: true,
    epubReader: false,
    video: false,
    hasPreview: true
  },

  defaultMode: 'light' as const,

  subscription: {
    stripePlanId: 'price_bible_monthly',
    price: '3.99',
    currency: 'GBP',
    features: [
      'Complete Bible Versions',
      'Daily Devotions',
      'Expert Commentary',
      'Study Resources',
      'Offline Access'
    ],
    usps: [
      {
        title: 'Complete Bible Access',
        description: 'Access multiple Bible versions, translations, and study resources in one place.',
        icon: 'book'
      },
      {
        title: 'Offline Access',
        description: 'Download Bible versions and study materials to read even without internet connection.',
        icon: 'download'
      },
      {
        title: 'Expert Commentary',
        description: 'Get insights from trusted biblical scholars and teachers.',
        icon: 'like'
      },
      {
        title: 'Daily Devotions',
        description: 'Access daily devotional content and spiritual growth resources.',
        icon: 'subscribe'
      }
    ]
  },

  urls: {
    terms: 'https://thebible.plus/terms',
    privacy: 'https://thebible.plus/privacy',
    support: 'https://thebible.plus/support'
  },

  seo: {
    title: 'The Bible Plus - Bible Versions & Spiritual Growth',
    description: 'Explore Bible versions, sermons, and spiritual teachings',
    ogImage: '/assets/images/bible/og-image.svg',
    defaults: {
      titles: {
        title: 'Bible & Spiritual Content | The Bible Plus',
        description: 'Discover Bible versions, sermons, and spiritual growth content',
        type: 'article'
      },
      categories: {
        title: 'Biblical Topics | The Bible Plus',
        description: 'Browse spiritual content by biblical topics and themes',
        type: 'website'
      },
      authors: {
        title: 'Speakers & Pastors | The Bible Plus',
        description: 'Learn from experienced pastors and spiritual teachers',
        type: 'website'
      },
      series: {
        title: 'Bible Studies | The Bible Plus',
        description: 'Follow comprehensive Bible study series',
        type: 'website'
      },
      casts: {
        title: 'Narrators | The Bible Plus',
        description: 'Professional narrators bringing scripture to life',
        type: 'website'
      }
    }
  }
} 